import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Button, Avatar, Tooltip,
  TablePagination,
} from '@mui/material';
import { CheckCircle, Cancel, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const STATUS_OPTS = ['Pending', 'Approved', 'Rejected'];

const STATUS_STYLE = {
  Pending: { bg: '#fff3e0', color: '#e65100' },
  Approved: { bg: '#e8f5e9', color: '#2e7d32' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
};

export default function Leave() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getLeavesStudents, { status: statusFilter });
    setLeaves(data?.leaves || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleApprove = async (leaveId, studentId, studentName) => {
    const data = await InvokeApi(Config.main_url + apiList.approveLeavesStudent, { leaveId });
    if (data?.code === '200') {
      // Send notification to student
      await InvokeApi(Config.main_url + apiList.sendStudentNotification, {
        student_id: studentId,
        title: '✅ Leave Approved',
        message: `Your leave request has been approved. Enjoy your time off! 🎉`,
        type: 'Leave',
      });
      load();
    } else alert(data?.message || 'Failed to approve');
  };

  const handleReject = async (leaveId, studentId, studentName) => {
    const data = await InvokeApi(Config.main_url + apiList.deleteLeavesStudent, { leaveId });
    if (data?.code === '200') {
      // Send notification to student
      await InvokeApi(Config.main_url + apiList.sendStudentNotification, {
        student_id: studentId,
        title: '❌ Leave Rejected',
        message: `Your leave request has been reviewed and unfortunately rejected. Please contact the school for more details.`,
        type: 'Leave',
      });
      load();
    } else alert(data?.message || 'Failed to reject');
  };

  const paginated = leaves.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Leave Manager</Typography>
          <Typography variant="body2" color="text.secondary">Manage leave applications</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}
          sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>
          Refresh
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3, }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {STATUS_OPTS.map(s => (
            <Chip key={s} label={s}
              onClick={() => setStatusFilter(s)}
              sx={{
                fontWeight: 700, cursor: 'pointer',
                background: statusFilter === s ? STATUS_STYLE[s].bg : '#f5f6fa',
                color: statusFilter === s ? STATUS_STYLE[s].color : 'text.secondary',
                border: statusFilter === s ? `1.5px solid ${STATUS_STYLE[s].color}` : '1.5px solid transparent',
              }}
            />
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {leaves.length} record{leaves.length !== 1 ? 's' : ''}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Student', 'Leave Type', 'From', 'To', 'Details', 'Status', ...(statusFilter === 'Pending' ? ['Actions'] : [])].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No {statusFilter.toLowerCase()} leave requests
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((l) => (
                    <TableRow key={l.ID} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 34, height: 34, background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 13 }}>
                            {(l.student_name || 'E')[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={13}>
                              {l.student_name || `Student #${l.student_id}`}
                            </Typography>
                            <Typography fontSize={11} color="text.secondary">ID: {l.user_id}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={l.leave_type || '—'} size="small"
                          sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {l.start_date ? dayjs(l.start_date).format('DD MMM YYYY') : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {l.end_date ? dayjs(l.end_date).format('DD MMM YYYY') : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 180 }}>
                        {l.leave_details || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={l.status} size="small"
                          sx={{
                            background: STATUS_STYLE[l.status]?.bg || '#f5f5f5',
                            color: STATUS_STYLE[l.status]?.color || '#333',
                            fontWeight: 600,
                          }} />
                      </TableCell>
                      {statusFilter === 'Pending' && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Approve">
                              <Button size="small" variant="contained" startIcon={<CheckCircle />}
                                onClick={() => handleApprove(l.ID, l.user_id, l.student_name)}
                                sx={{ background: '#2e7d32', borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.5, '&:hover': { background: '#1b5e20' } }}>
                                Approve
                              </Button>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <Button size="small" variant="contained" startIcon={<Cancel />}
                                onClick={() => handleReject(l.ID, l.user_id, l.student_name)}
                                sx={{ background: '#c62828', borderRadius: 1.5, fontSize: 11, fontWeight: 700, px: 1.5, '&:hover': { background: '#b71c1c' } }}>
                                Reject
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={leaves.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>
    </Box>
  );
}
